import React, { createContext, useContext, useState, ReactNode } from 'react';
import { MessagePayload } from 'firebase/messaging';
import { PushNotificationBanner } from '@/shared/components/core-ui/push-notification-banner';

interface PushNotificationContextType {
  showNotification: (notification: MessagePayload) => void;
  hideNotification: () => void;
  currentNotification: MessagePayload | null;
}

const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined);

interface PushNotificationProviderProps {
  children: ReactNode;
  autoHideDuration?: number;
}

export const PushNotificationProvider: React.FC<PushNotificationProviderProps> = ({
  children,
  autoHideDuration = 20000
}) => {
  const [currentNotification, setCurrentNotification] = useState<MessagePayload | null>(null);

  const showNotification = (notification: MessagePayload) => {
    setCurrentNotification(notification);
  };

  const hideNotification = () => {
    setCurrentNotification(null);
  };

  const contextValue: PushNotificationContextType = {
    showNotification,
    hideNotification,
    currentNotification
  };

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
      <PushNotificationBanner
        notification={currentNotification}
        onClose={hideNotification}
        autoHideDuration={autoHideDuration}
      />
    </PushNotificationContext.Provider>
  );
};

export const usePushNotification = (): PushNotificationContextType => {
  const context = useContext(PushNotificationContext);
  if (context === undefined) {
    throw new Error('usePushNotification must be used within a PushNotificationProvider');
  }
  return context;
};
